import React, { useState } from "react";
// primitives
import {
  Body5MediumTypography,
  Body6RegularTypography,
  Heading5SemiboldTypography,
} from "../../primitives/Typography/typographyVariants";
// components
import { colorPalette } from "../../theme/colors";
import FlexRow from "../../components/Layout/FlexRow/FlexRow";
import FlexColumn from "../../components/Layout/FlexColumn/FlexColumn";
import { ActionButton } from "../../components/ActionButton/ActionButton";
// constants
import { IMAGE_PREFIX, transformMedia } from "../../utils/constants";

// types
import { CartItemProps } from "./CartItem.types";
// styles
import { ProductImage } from "./CartItem.styles";
import { Icon } from "../Icon/Icon";
import Tags from "../Tags/Tags";

export const CartItem: React.FC<CartItemProps> = ({
  item,
  variant = "default",
  status,
  onQuantityChange,
  onDelete,
  disabled = false,
  dataCy = "cart-item",
  tagIcon,
  tagTextColor,
  tagBackgroundColor,
  tagIconColor,
  isLastItem = false,
  onWishlistClick,
  onClickProductImage,
  isWishlisted = false,
  isOutOfStock = false,
}) => {
  const { id, quantity } = item;
  const { option_name, option_type } = item?.product || {};
  const { mrp, price, thumbnail_url, name, description } =
    item?.product?.product || {};

  // Convert mrp and price to numbers, handling both string and number types
  const mrpNum =
    mrp != null
      ? typeof mrp === "string"
        ? parseFloat(mrp)
        : Number(mrp)
      : null;
  const priceNum =
    price != null
      ? typeof price === "string"
        ? parseFloat(price)
        : Number(price)
      : null;

  const [count, setCount] = useState(quantity || 1);

  const handleIncrement = () => {
    if (disabled) return;

    const newCount = count + 1;
    setCount(newCount);
    onQuantityChange?.(id, newCount);
  };

  const handleDecrement = () => {
    if (disabled) return;

    if (count > 1) {
      const newCount = count - 1;
      setCount(newCount);
      onQuantityChange?.(id, newCount);
    } else {
      onDelete?.(id);
    }
  };
  const sanitize_price = (num: number | string | null | undefined) => {
    if (num == null) {
      return "0";
    }
    const number = typeof num === "string" ? parseFloat(num) : Number(num);
    if (isNaN(number)) {
      return "0";
    }
    return Math.round(number).toLocaleString();
  };

  const discountPercentage =
    mrpNum != null && priceNum != null && mrpNum > priceNum
      ? Math.round(((mrpNum - priceNum) / mrpNum) * 100)
      : 0;

  return (
    <FlexRow
      gap={4}
      p={4}
      alignItems="flex-start"
      backgroundColor="#FFF"
      borderBottom={
        !isLastItem ? `1px dashed ${colorPalette.white[100]}` : "none"
      }
      data-cy={dataCy}
    >
      <ProductImage
        src={
          transformMedia(thumbnail_url) ||
          transformMedia(`${IMAGE_PREFIX}/Wishlink-logo.png`)
        }
        alt={`${name} product image`}
        data-cy={`${dataCy}-image`}
        isOutOfStock={isOutOfStock}
        onClick={() => onClickProductImage?.(id)}
      />
      <FlexColumn gap={2} width="unset" flex={1}>
        <Heading5SemiboldTypography
          color={colorPalette.popBlack[500]}
          lineClamp={1}
          overflow="hidden"
          textOverflow="ellipsis"
          textTransform="uppercase"
          maxWidth="150px"
          data-cy={`${dataCy}-name`}
        >
          {name}
        </Heading5SemiboldTypography>
        <Body6RegularTypography
          overflow="hidden"
          textOverflow="ellipsis"
          lineClamp={1}
          data-cy={`${dataCy}-description`}
          color={colorPalette.popBlack[500]}
        >
          {description}
        </Body6RegularTypography>
        {option_type && option_name && (
          <FlexRow gap={4}>
            <Body6RegularTypography color={colorPalette.black[100]}>
              {option_type} :
            </Body6RegularTypography>
            <Body6RegularTypography data-cy={`${dataCy}-option`}>
              {option_name}
            </Body6RegularTypography>
          </FlexRow>
        )}
        {(mrpNum != null || priceNum != null) && variant === "wishlist" && (
          <FlexRow gap={4} alignItems="flex-start" width="unset">
            {mrpNum != null && priceNum != null && mrpNum > priceNum && (
              <Body5MediumTypography
                textDecoration="line-through"
                color={colorPalette.popBlack[100]}
                data-cy={`${dataCy}-mrp`}
              >
                &#8377; {sanitize_price(mrpNum)}
              </Body5MediumTypography>
            )}
            <Body5MediumTypography color={colorPalette.popBlack[500]}>
              &#8377; {sanitize_price(priceNum)}
            </Body5MediumTypography>
          </FlexRow>
        )}
        {variant === "wishlist" && status && (
          <Tags
            variant="secondary"
            label={status || ""}
            color={tagTextColor}
            backgroundColor={tagBackgroundColor}
            iconColor={tagIconColor}
          />
        )}
      </FlexColumn>

      {variant === "track" && (
        <Tags
          variant="primary"
          label={status || ""}
          color={tagTextColor}
          backgroundColor={tagBackgroundColor}
          icon={tagIcon}
          iconColor={tagIconColor}
        />
      )}
      {variant === "default" && (
        <FlexColumn gap={4} width="unset">
          <FlexRow justifyContent="flex-end">
            <ActionButton
              variant="quantity"
              quantity={count}
              onIncrement={handleIncrement}
              onDecrement={handleDecrement}
              disabled={disabled}
              dataCy={`${dataCy}-quantity-control`}
            >
              {count}
            </ActionButton>
          </FlexRow>
          {(mrp || price) && (
            <FlexRow gap={4} alignItems="flex-start" width="unset">
              {mrp && mrp > price && (
                <Body5MediumTypography
                  textDecoration="line-through"
                  color={colorPalette.popBlack[100]}
                  data-cy={`${dataCy}-mrp`}
                >
                  &#8377; {sanitize_price(mrp)}
                </Body5MediumTypography>
              )}
              <Body5MediumTypography color={colorPalette.popBlack[500]}>
                &#8377; {sanitize_price(price)}
              </Body5MediumTypography>
            </FlexRow>
          )}
        </FlexColumn>
      )}
      {variant === "wishlist" && (
        <FlexColumn
          gap={4}
          alignItems="center"
          justifyContent="center"
          height="100%"
          width="unset"
        >
          <FlexRow justifyContent="flex-end" gap={20} pl={8} pr={20}>
            {onWishlistClick && (
              <Icon
                name={
                  isWishlisted
                    ? "fa-solid fa-bookmark"
                    : "fa-regular fa-bookmark"
                }
                size="14"
                height="18"
                width="13"
                color={
                  isWishlisted
                    ? "text.wishlink.default"
                    : "text.default.default"
                }
                onClick={() => onWishlistClick?.(id)}
              />
            )}
            {onDelete && (
              <Icon
                name="fa-regular fa-trash-can"
                size="14"
                height="18"
                width="13"
                color="text.default.default"
                onClick={() => onDelete?.(id)}
              />
            )}
          </FlexRow>
        </FlexColumn>
      )}
    </FlexRow>
  );
};
